<script setup lang="ts">
import type { CheckoutDatacenter } from '~/composables/useDatacenterDisplay'

const props = defineProps<{
  modelValue: number | null
  datacenters: CheckoutDatacenter[]
  loading?: boolean
}>()
const emit = defineEmits<{ 'update:modelValue': [value: number | null] }>()

const { countryName, countryFlag } = useDatacenterDisplay()

const available = computed(() =>
  props.datacenters.filter((datacenter) => datacenter.deployable !== false)
)
const unavailable = computed(() =>
  props.datacenters.filter((datacenter) => datacenter.deployable === false)
)

/**
 * A checkout should not manufacture a decision. When only one location can
 * accept a server, select it and present it as information instead of a radio
 * button the customer has to click before they can see a plan.
 */
function reconcileSelection() {
  if (props.loading) return

  const locations = available.value
  if (locations.length === 1) {
    if (props.modelValue !== locations[0]!.id) {
      emit('update:modelValue', locations[0]!.id)
    }
    return
  }

  // A location that has stopped being deployable cannot stay selected.
  if (
    props.modelValue != null &&
    !locations.some((location) => location.id === props.modelValue)
  ) {
    emit('update:modelValue', null)
  }
}

/**
 * Deliberately NOT `immediate`, and the first pass runs `onMounted`.
 *
 * This emits — it mutates the parent — and Vue's SSR renderer is sequential, so
 * running it during setup made the selection visible to the steps rendered
 * BELOW this card but not to the same steps during hydration, where the
 * parent's re-render is batched. That is a genuine hydration mismatch on the
 * public configurator, which is server-rendered, and it moved around as it was
 * chased: first the step cards, then the blocker checklist.
 *
 * Running it after mount makes the server's markup and the client's first
 * render identical by construction, and costs nothing: the auto-selection is a
 * convenience that lands long before anyone can act on it.
 */
watch([available, () => props.loading], reconcileSelection)
onMounted(reconcileSelection)

</script>

<template>
  <div :aria-busy="loading">
    <div
      v-if="loading"
      class="grid grid-cols-1 gap-3 sm:grid-cols-2"
      aria-hidden="true"
    >
      <USkeleton v-for="n in 2" :key="n" class="h-20 w-full rounded-xl" />
    </div>

    <UAlert
      v-else-if="datacenters.length === 0"
      color="error"
      variant="subtle"
      icon="i-lucide-triangle-alert"
      :title="$t('billing.page.checkout.noDatacenters')"
    />

    <UAlert
      v-else-if="available.length === 0"
      color="warning"
      variant="subtle"
      icon="i-lucide-triangle-alert"
      :title="$t('billing.page.checkout.noDeployableDatacenters')"
    />

    <div v-else class="space-y-3">
      <!-- One available location is useful context, but not a decision. -->
      <div
        v-if="available.length === 1"
        class="flex min-h-20 items-center gap-3 rounded-xl border border-success/35 bg-success/5 p-4 dark:bg-success/10"
        aria-live="polite"
      >
        <span
          class="flex size-11 shrink-0 items-center justify-center rounded-xl bg-default text-2xl shadow-sm ring-1 ring-default"
          aria-hidden="true"
        >
          {{ countryFlag(available[0]!) }}
        </span>
        <div class="min-w-0 flex-1">
          <p class="font-semibold text-highlighted">{{ available[0]!.name }}</p>
          <p class="flex flex-wrap items-center gap-x-1 text-sm text-toned">
            <span>{{ countryName(available[0]!) }}</span>
            <!-- `success-700`, not the raw `success` token: at 500 it measures
                 2.07:1 against the soft success tint this sits on, and white on
                 a solid success fill is no better at 2.37:1. The darker step is
                 the only one of the three that clears 4.5:1. -->
            <span class="text-success-800 dark:text-success-300 sm:hidden">
              · {{ $t('billing.page.checkout.location.availableNow') }}
            </span>
          </p>
        </div>
        <UBadge
          color="success"
          variant="soft"
          icon="i-lucide-circle-check"
          class="hidden shrink-0 sm:inline-flex text-success-800 dark:text-success-300"
        >
          {{ $t('billing.page.checkout.location.availableNow') }}
        </UBadge>
      </div>

      <!--
        `role="group"` + `aria-pressed`, NOT `radiogroup`/`radio`.

        Claiming the radio role owes the radio keyboard contract — a roving
        tabindex, arrow keys moving between options, exactly one stop in the tab
        order — and none of that existed: Tab walked every location one at a
        time and the arrow keys did nothing, so the promise the markup made to a
        screen reader was one the page did not keep. The plan table beside it
        already uses plain buttons with `aria-pressed`, so this also makes the
        two pickers on the same page behave the same way.
      -->
      <div
        v-else
        class="grid grid-cols-1 gap-3 sm:grid-cols-2"
        role="group"
        :aria-label="$t('billing.page.checkout.steps.location')"
      >
        <button
          v-for="datacenter in available"
          :key="datacenter.id"
          type="button"
          :aria-pressed="modelValue === datacenter.id"
          :class="[
            'group flex min-h-20 items-center gap-3 rounded-xl border-2 p-3.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--ui-bg)]',
            modelValue === datacenter.id
              ? 'border-primary bg-primary/10 shadow-sm dark:bg-primary/15'
              : 'border-default bg-default hover:border-primary/60 hover:bg-elevated',
          ]"
          @click="emit('update:modelValue', datacenter.id)"
        >
          <span
            class="flex size-11 shrink-0 items-center justify-center rounded-xl bg-elevated text-2xl ring-1 ring-default"
            aria-hidden="true"
          >
            {{ countryFlag(datacenter) }}
          </span>
          <span class="min-w-0 flex-1">
            <span class="block font-semibold text-highlighted">
              {{ datacenter.name }}
            </span>
            <span class="block text-sm text-toned">
              {{ countryName(datacenter) }}
            </span>
          </span>
          <UIcon
            :name="
              modelValue === datacenter.id
                ? 'i-lucide-circle-check'
                : 'i-lucide-circle'
            "
            :class="
              modelValue === datacenter.id
                ? 'text-primary'
                : 'text-muted group-hover:text-toned'
            "
            class="size-5 shrink-0"
          />
        </button>
      </div>

      <p
        v-if="unavailable.length > 0"
        class="flex items-start gap-1.5 text-xs text-toned"
      >
        <UIcon name="i-lucide-clock-3" class="mt-px size-3.5 shrink-0" />
        <span>
          {{
            $t('billing.page.checkout.location.unavailable', {
              locations: unavailable.map((location) => location.name).join(', '),
            })
          }}
        </span>
      </p>
    </div>
  </div>
</template>
